/**
 * The phone roster: every region as a card, ordered by the chosen sort
 * (BON-534).
 *
 * Deliberately FLAT — no carets. On a phone the design drills into a region
 * rather than expanding it in place, so a sub-region is reached by opening its
 * parent, which is also what keeps the stack short enough to scan.
 */
import { useMemo, useState } from 'react';

import type { OverviewRegionRow } from './overview-types';
import { RegionRosterTile } from './RegionRosterTile';
import { ROSTER_SORT_OPTIONS } from './roster-columns';

export interface RegionRosterStackProps {
  rows: OverviewRegionRow[];
}

export function RegionRosterStack({ rows }: RegionRosterStackProps) {
  const [sortId, setSortId] = useState(ROSTER_SORT_OPTIONS[0]?.id);
  const sort = ROSTER_SORT_OPTIONS.find((o) => o.id === sortId) ?? ROSTER_SORT_OPTIONS[0];

  // Top-level regions only; a sub-region is reached by opening its parent.
  const shown = useMemo(
    () =>
      rows
        .filter((r) => r.parentId === null)
        .slice()
        .sort(sort.compare),
    [rows, sort],
  );

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--nb-subtle)',
          }}
        >
          Regions · by {sort.label.toLowerCase()}
        </h3>
        <div style={{ flex: 1 }} />
        {ROSTER_SORT_OPTIONS.length > 1 && (
          <div
            role="group"
            aria-label="Sort regions"
            style={{
              display: 'flex',
              background: 'var(--nb-surface)',
              border: '1px solid var(--nb-border)',
              borderRadius: 999,
              padding: 2,
            }}
          >
            {ROSTER_SORT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={option.id === sortId}
                onClick={() => setSortId(option.id)}
                style={{
                  height: 22,
                  padding: '0 10px',
                  border: 0,
                  cursor: 'pointer',
                  fontSize: 10.5,
                  fontWeight: 600,
                  borderRadius: 999,
                  color: option.id === sortId ? 'var(--nb-bark-dark)' : 'var(--nb-subtle)',
                  background: option.id === sortId ? 'var(--nb-bg)' : 'transparent',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {shown.map((row) => (
        <RegionRosterTile key={row.id} row={row} />
      ))}
    </section>
  );
}
